import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';

function ClassesContentPage({ showModal, setShowModal, onAddClassRef }) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const [formData, setFormData] = useState({});
  const [promptsModal, setPromptsModal] = useState(false);
  const [selectedPromptsClass, setSelectedPromptsClass] = useState(null);
  const [showPromptForm, setShowPromptForm] = useState(false);
  const [promptFormData, setPromptFormData] = useState({ prompt_text: '', version_notes: '' });

  // Для нового функционала в модалке редактирования
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [selectedPromptVersion, setSelectedPromptVersion] = useState(null);
  const [originalPromptText, setOriginalPromptText] = useState('');

  const fetchClasses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getClasses({ with_prompts: true });
      setClasses(data);
    } catch (error) {
      toast.error('Ошибка загрузки классификатора');
      console.error('Error loading classes:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  const handleCreate = useCallback(() => {
    setSelectedClass(null);
    setFormData({
      name: '',
      example_text: '',
      class_prompt: '',
      class_metadata: { group_type: 'info' },
    });
    setShowModal(true);
  }, [setShowModal]);

  useEffect(() => {
    if (onAddClassRef) {
      onAddClassRef.current = handleCreate;
    }
  }, [onAddClassRef, handleCreate]);

  const handleEdit = async (item) => {
    try {
      const classData = await api.getClass(item.id);
      setSelectedClass(classData);
      setFormData({ ...classData });

      // Загружаем все промпты для dropdown
      if (classData.prompts && classData.prompts.length > 0) {
        setAvailablePrompts(classData.prompts);

        // Находим активный промпт
        const activePrompt = classData.prompts.find(p => p.is_active);
        if (activePrompt) {
          setSelectedPromptVersion(activePrompt.id);
          setOriginalPromptText(activePrompt.prompt_text);
          // Устанавливаем текст активного промпта в formData
          setFormData(prev => ({ ...prev, class_prompt: activePrompt.prompt_text }));
        }
      } else {
        setAvailablePrompts([]);
        setSelectedPromptVersion(null);
        setOriginalPromptText('');
      }

      setShowModal(true);
    } catch (error) {
      toast.error('Ошибка загрузки данных класса');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот класс?')) return;
    try {
      await api.deleteClass(itemId);
      toast.success('Класс удален');
      fetchClasses();
    } catch (error) {
      toast.error(error.message || 'Ошибка при удалении класса');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Название класса обязательно для заполнения');
      return;
    }

    try {
      // Проверяем изменился ли текст промпта (только для редактирования)
      const promptChanged = selectedClass &&
                           formData.class_prompt &&
                           formData.class_prompt.trim() !== '' &&
                           formData.class_prompt.trim() !== originalPromptText.trim();

      // 1. Если изменился промпт - создаем новую версию
      if (promptChanged) {
        await api.createPrompt(selectedClass.id, {
          prompt_text: formData.class_prompt.trim(),
          version_notes: 'Обновлено через редактор класса'
        });
        toast.success('✅ Создана новая версия промпта');
      }

      // 2. Обновляем метаданные класса (название, пример, чекбоксы)
      // Нормализуем class_metadata: из group_type выставляем is_rop и to_manager (бэкенд и бот от них зависят)
      const rawMeta = formData.class_metadata || { group_type: 'info' };
      const gt = rawMeta.group_type || (rawMeta.is_rop ? 'rop' : rawMeta.to_manager ? 'manager' : 'info');
      const class_metadata = {
        ...rawMeta,
        group_type: gt,
        is_rop: gt === 'rop',
        to_manager: gt === 'rop' || gt === 'manager',
      };
      const payload = {
        name: formData.name.trim(),
        example_text: formData.example_text ? formData.example_text.trim() : undefined,
        // Для создания класса отправляем промпт, для обновления - нет (версии управляются отдельно)
        class_prompt: !selectedClass && formData.class_prompt ? formData.class_prompt.trim() : undefined,
        class_metadata,
      };

      const apiCall = selectedClass
        ? api.updateClass(selectedClass.id, payload)
        : api.createClass(payload);

      await apiCall;

      if (!promptChanged) {
        toast.success(selectedClass ? 'Класс обновлен' : 'Класс создан');
      }

      setShowModal(false);
      fetchClasses();
    } catch (error) {
      toast.error(error.message || 'Ошибка при сохранении');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Обработчик выбора версии из dropdown
  const handleVersionChange = async (promptId) => {
    const promptIdNum = parseInt(promptId);
    const selectedPrompt = availablePrompts.find(p => p.id === promptIdNum);

    if (!selectedPrompt) return;

    try {
      // Активируем выбранную версию
      await api.setActivePrompt(selectedClass.id, promptIdNum);
      toast.success(`Активирована версия v${selectedPrompt.version}`);

      // Обновляем состояние
      setSelectedPromptVersion(promptIdNum);
      setOriginalPromptText(selectedPrompt.prompt_text);
      setFormData(prev => ({ ...prev, class_prompt: selectedPrompt.prompt_text }));

      // Обновляем список промптов (is_active флаги)
      const updatedClassData = await api.getClass(selectedClass.id);
      setAvailablePrompts(updatedClassData.prompts);
      fetchClasses(); // Обновляем таблицу
    } catch (error) {
      toast.error(error.message || 'Ошибка активации версии');
    }
  };

  // ПРОМПТЫ
  const handlePromptsClick = async (classItem) => {
    try {
      const classWithPrompts = await api.getClass(classItem.id);
      setSelectedPromptsClass(classWithPrompts);
      setPromptsModal(true);
    } catch (error) {
      toast.error('Ошибка загрузки промптов');
    }
  };

  const handleCreatePromptClick = () => {
    setShowPromptForm(true);
    setPromptFormData({ prompt_text: '', version_notes: '' });
  };

  const handleDeletePrompt = async (promptId) => {
    const prompt = selectedPromptsClass.prompts.find(p => p.id === promptId);
    if (!window.confirm(`Удалить версию v${prompt?.version || ''}?`)) return;

    try {
      await api.deletePrompt(selectedPromptsClass.id, promptId);
      toast.success('Версия промпта удалена');
      const classWithPrompts = await api.getClass(selectedPromptsClass.id);
      setSelectedPromptsClass(classWithPrompts);
    } catch (error) {
      toast.error(error.message || 'Ошибка удаления');}
  };

  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editPromptData, setEditPromptData] = useState({ prompt_text: '', version_notes: '' });

  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt);
    setEditPromptData({
      prompt_text: prompt.prompt_text,
      version_notes: prompt.version_notes || ''
    });
    setShowPromptForm(true);
  };

  const handleEditPromptSubmit = async (e) => {
    e.preventDefault();
    if (!editPromptData.prompt_text?.trim()) {
      toast.error('Текст промпта обязателен');
      return;
    }

    try {
      await api.updateClassPrompt(selectedPromptsClass.id, editingPrompt.id, {
        prompt_text: editPromptData.prompt_text.trim(),
        version_notes: editPromptData.version_notes?.trim() || undefined
      });
      toast.success('✅ Промпт обновлен');
      const classWithPrompts = await api.getClass(selectedPromptsClass.id);
      setSelectedPromptsClass(classWithPrompts);
      setEditingPrompt(null);
      setShowPromptForm(false);
    } catch (error) {
      toast.error(error.message || 'Ошибка обновления');
    }
  };

  const handleSetActivePrompt = async (promptId) => {
    try {
      await api.setActivePrompt(selectedPromptsClass.id, promptId);
      toast.success('✅ Промпт активирован');
      const classWithPrompts = await api.getClass(selectedPromptsClass.id);
      setSelectedPromptsClass(classWithPrompts);
      fetchClasses(); // Обновляем таблицу
    } catch (error) {
      toast.error(error.message || 'Ошибка активации');
    }
  };

  const handlePromptSubmit = async (e) => {
    e.preventDefault();
    if (!promptFormData.prompt_text?.trim()) {
      toast.error('Текст промпта обязателен');
      return;
    }



    try {
      await api.createPrompt(selectedPromptsClass.id, {
        prompt_text: promptFormData.prompt_text.trim(),
        version_notes: promptFormData.version_notes?.trim() || undefined
      });
      toast.success('✅ Новая версия промпта создана и активирована');
      setPromptsModal(false);
      setShowPromptForm(false);
      fetchClasses();
    } catch (error) {
      toast.error(error.message || 'Ошибка создания промпта');
    }
  };

  const handlePromptFormChange = (field, value) => {
      if (editingPrompt) {
        setEditPromptData(prev => ({ ...prev, [field]: value }));
      } else {
        setPromptFormData(prev => ({ ...prev, [field]: value }));
      }
    };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        color: '#6b7280'
      }}>
        Загрузка классификатора...
      </div>
    );
  }

  return (
    <div>
      {/* ПУСТОЙ СОСТОЯНИЕ */}
      {classes.length === 0 ? (
        <div className="empty-state">
          <div style={{
            fontSize: '48px',
            marginBottom: '16px',
            opacity: 0.3
          }}>
            🏷️
          </div>
          <h3>Нет классов классификатора</h3>
          <p style={{ color: '#6b7280', marginBottom: '20px' }}>
            Создайте первый класс для начала работы с классификатором сообщений
          </p>
          <button onClick={handleCreate} className="btn btn-primary">
            Создать первый класс
          </button>
        </div>
      ) : (
        /* ТАБЛИЦА */
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Класс</th>
                <th>Условие классификации</th>
                <th>Версия промпта</th>
                <th>Тип группы</th>
                <th>Дата создания</th>
                <th>Дата обновления</th>
                <th className="w-20 text-center">Действия</th>
              </tr>
            </thead>
            <tbody>
              {classes.map((item) => (
                <tr key={item.id}>
                  <td className="text-center">{item.id}</td>
                  <td>
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">
                      {item.name}
                    </span>
                  </td>
                  <td className="text-sm text-gray-600"
                      style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={item.example_text}>
                    {item.example_text || '-'}
                  </td>
                  <td className="text-sm">
                    {item.active_prompt_version !== null && item.active_prompt_version !== undefined ? (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        v{item.active_prompt_version}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="text-sm">
                    {(() => {
                      const md = item.class_metadata;
                      const type = md?.group_type || (md?.is_rop ? 'rop' : md?.to_manager ? 'manager' : 'info');
                      if (type === 'rop') return <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">РОП</span>;
                      if (type === 'manager') return <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">Менеджер</span>;
                      return <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">Информационный</span>;
                    })()}
                  </td>
                  <td className="text-sm text-gray-500">
                    {new Date(item.created_at).toLocaleDateString('ru-RU', {
                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </td>
                  <td className="text-sm text-gray-500">
                    {new Date(item.updated_at).toLocaleDateString('ru-RU', {
                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </td>
                  <td className="text-center">
                    <div className="table-actions">
                      <button onClick={() => handleEdit(item)} className="btn btn-outline btn-small">
                        Редактировать
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="btn btn-danger btn-small ml-1"
                        disabled={classes.length <= 2}
                        title={classes.length <= 2 ? "Нельзя удалить: должно остаться минимум 2 класса" : ""}
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {classes.length <= 2 && (
            <div style={{
              marginTop: '16px', padding: '12px', backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b', borderRadius: '6px', fontSize: '14px', color: '#92400e'
            }}>
              ⚠️ В системе должно быть минимум 2 класса классификатора. Текущее количество: {classes.length}
            </div>
          )}
        </div>
      )}

      {/* МОДАЛКА КЛАССОВ */}
      {showModal && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)}
               title={selectedClass ? 'Редактировать класс' : 'Добавить класс'}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {selectedClass && (
              <div className="form-group">
                <label>ID</label>
                <input type="text" disabled value={selectedClass.id}
                       style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }} />
              </div>
            )}

            <div className="form-group">
              <label>Название класса *</label>
              <input type="text" required value={formData.name || ''}
                     onChange={(e) => handleFormChange('name', e.target.value)}
                     placeholder="Например: Рассчитать стоимость" style={{ width: '100%' }} />
            </div>

            <div className="form-group">
              <label>Пример текста</label>
              <textarea rows="4" value={formData.example_text || ''}
                        onChange={(e) => handleFormChange('example_text', e.target.value)}
                        placeholder="Пример текста, который относится к этому классу..."
                        style={{ width: '100%', resize: 'vertical' }} />
            </div>

            {/* Если редактируем класс и есть промпты - показываем dropdown */}
            {selectedClass && availablePrompts.length > 0 ? (
              <div className="form-group">
                <label>Версия промпта</label>
                <select
                  value={selectedPromptVersion || ''}
                  onChange={(e) => handleVersionChange(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                >
                  {availablePrompts.map(prompt => (
                    <option key={prompt.id} value={prompt.id}>
                      v{prompt.version} - {new Date(prompt.created_at).toLocaleDateString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })} {prompt.is_active ? '(активная)' : ''}
                      {prompt.version_notes ? ` - ${prompt.version_notes}` : ''}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Выбор версии сразу активирует её
                </small>
              </div>
            ) : null}

            <div className="form-group">
              <label>
                {selectedClass && availablePrompts.length > 0
                  ? 'Текст промпта'
                  : 'Промпт класса (первая версия)'}
              </label>
              <textarea rows="6" value={formData.class_prompt || ''}
                        onChange={(e) => handleFormChange('class_prompt', e.target.value)}
                        placeholder="Промпт для классификации этого класса..."
                        style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }} />
              <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                {selectedClass && availablePrompts.length > 0
                  ? 'Изменение текста создаст новую версию при сохранении'
                  : 'Создаст первую версию промпта автоматически'}
              </small>
            </div>

            <div className="form-group">
              <label>Тип группы</label>
              <select
                value={formData.class_metadata?.group_type || (formData.class_metadata?.is_rop ? 'rop' : formData.class_metadata?.to_manager ? 'manager' : 'info')}
                onChange={(e) => handleFormChange('class_metadata', {
                  ...formData.class_metadata, group_type: e.target.value
                })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
              >
                <option value="info">Информационный — группа не создается</option>
                <option value="manager">Менеджер — создается группа с менеджером</option>
                <option value="rop">РОП — создается группа + ведет на РОПа</option>
              </select>
            </div>

            <div className="modal-footer">
              <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                Отмена
              </button>
              <button type="submit" className="btn btn-primary ml-2">
                {selectedClass ? 'Обновить' : 'Создать'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* МОДАЛКА ПРОМПТОВ - ПОЛНАЯ */}
      {promptsModal && selectedPromptsClass && (
        <Modal isOpen={promptsModal} onClose={() => setPromptsModal(false)}
               title={`Промпты класса: ${selectedPromptsClass.name}`}>
          <div className="space-y-4">
            {/* Заголовок + кнопка */}
            <div className="flex justify-between items-center">
              <h4 className="font-semibold">
                Версии промптов ({selectedPromptsClass.prompts?.length || 0})
                {selectedPromptsClass.active_prompt && (
                  <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    Активная: v{selectedPromptsClass.active_prompt.version}
                  </span>
                )}
              </h4>
              <button onClick={handleCreatePromptClick} className="btn btn-primary btn-small">
                + Новая версия
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto p-3 border rounded-lg bg-gray-50">
              {selectedPromptsClass.prompts?.length ? (
                selectedPromptsClass.prompts.map((prompt) => (
                  <div key={prompt.id} className="p-3 border-b last:border-b-0 hover:bg-white rounded-lg mb-2">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          prompt.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          v{prompt.version} {prompt.is_active && '✅ АКТИВНАЯ'}
                        </span>
                        {prompt.version_notes && (
                          <span className="ml-2 text-xs text-gray-500 italic">"{prompt.version_notes}"</span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {/* ✅ АКТИВАЦИЯ (без изменений) */}
                        <button
                          onClick={() => handleSetActivePrompt(prompt.id)}
                          className={`btn btn-xs text-xs p-1 ${
                            prompt.is_active
                              ? 'bg-green-100 text-green-800 border border-green-300'
                              : 'btn-primary hover:bg-blue-600'
                          }`}
                          disabled={prompt.is_active}
                        >
                          {prompt.is_active ? '✅ Активна' : '⭐ Активировать'}
                        </button>

                        {/* ✅ НОВЫЕ КНОПКИ (только НЕактивные) */}
                        {!prompt.is_active && (
                          <>
                            {/* ✏️ РЕДАКТИРОВАНИЕ */}
                            <button
                              onClick={() => handleEditPrompt(prompt)}
                              className="btn btn-warning btn-xs text-xs p-1 hover:bg-yellow-600"
                              title="Редактировать промпт"
                            >
                              ✏️
                            </button>

                            {/* 🗑️ УДАЛЕНИЕ */}
                            <button
                              onClick={() => handleDeletePrompt(prompt.id)}
                              className="btn btn-danger btn-xs text-xs p-1 hover:bg-red-600"
                              title="Удалить версию"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* ✅ ПРОКРУТКА + ПОЛНЫЙ ТЕКСТ */}
                    <div className="text-xs font-mono bg-white p-3 rounded border leading-relaxed group relative">
                      <div
                        className="max-h-24 overflow-y-auto hover:max-h-64 transition-all duration-300 pr-8 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
                        style={{ scrollbarWidth: 'thin' }}
                        title={prompt.prompt_text}
                      >
                        {prompt.prompt_text}
                      </div>
                      {/* ✅ КНОПКА "ПОКАЗАТЬ ПОЛНОСТЬЮ" */}
                      <button
                        className="absolute bottom-2 right-2 text-xs text-gray-500 hover:text-gray-800 opacity-0 group-hover:opacity-100 transition-all"
                        onClick={() => navigator.clipboard.writeText(prompt.prompt_text).then(() => toast.success('Скопировано!'))}
                        title="Скопировать промпт"
                      >
                        📋
                      </button>
                    </div>
                  </div>
              ))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  📝 Нет версий промптов<br/>
                  <small>Нажмите "+" чтобы создать первую версию</small>
                </div>
              )}
            </div>

            {/* ✅ ФОРМА СОЗДАНИЯ ПРОМПТА */}
            {showPromptForm && (
              <form onSubmit={editingPrompt ? handleEditPromptSubmit : handlePromptSubmit} className="space-y-4 mt-4 p-4 border rounded-lg bg-blue-50">
                <h5 className="font-semibold text-blue-800">
                  {editingPrompt
                    ? `Редактировать v${editingPrompt.version}`
                    : `Новая версия промпта (v${selectedPromptsClass.prompts?.length + 1 || 1})`
                  }
                </h5>

                <div className="form-group">
                  <label className="font-medium">Текст промпта <span className="text-red-500">*</span></label>
                  <textarea
                    rows="6"
                    value={editingPrompt ? editPromptData.prompt_text : promptFormData.prompt_text}
                    onChange={(e) => handlePromptFormChange('prompt_text', e.target.value)}
                    placeholder="Введите новый системный промпт для классификатора..."
                    className="w-full p-3 border rounded-lg font-monospace resize-vertical focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Примечания к версии (опционально)</label>
                  <input
                    type="text"
                    value={editingPrompt ? editPromptData.version_notes : promptFormData.version_notes}
                    onChange={(e) => handlePromptFormChange('version_notes', e.target.value)}
                    placeholder="Что изменилось в этой версии?"
                    className="w-full p-3 border rounded-lg"
                    maxLength={200}
                  />
                  <small className="text-gray-500">
                    {(editingPrompt ? editPromptData.version_notes : promptFormData.version_notes)?.length || 0}/200
                  </small>
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="submit" className="btn btn-primary btn-small flex-1">
                    ✅ Создать и активировать
                  </button>
                  <button type="button"
                          onClick={() => { setShowPromptForm(false); }}
                          className="btn btn-secondary btn-small">
                    Отмена
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="modal-footer mt-4 pt-4 border-t">
            <button onClick={() => {
              setPromptsModal(false);
              setShowPromptForm(false);
            }} className="btn btn-secondary">
              Закрыть
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export { ClassesContentPage as ClassesComponent };
export default ClassesContentPage;
